<!--
  Renders a list of CampaignNextStep objects (Key/Action/Label/Description/Url/External) and runs them.

  Shared on purpose: the campaign detail's "Suggestions" and the audience "Content gaps" audit return the
  SAME server contract and fire through the same server-side runner, so the click behaviour — especially the
  "link steps open in a new tab, internal routes resolved to a full href" rule — lives in one place.

  The host owns the store call: this emits `run(step)` and exposes `fired`/`unfire` so a failed dispatch can
  be rolled back.
-->
<template>
  <div>
    <div
      v-if="loading"
      class="d-flex align-center text-caption grey--text"
    >
      <v-progress-circular
        indeterminate
        size="18"
        width="2"
        color="mcsprimary"
        class="mr-2"
      />
      {{ loadingText }}
    </div>

    <template v-else>
      <div
        v-if="!steps.length"
        class="text-caption grey--text"
      >
        {{ emptyText }}
      </div>

      <div
        v-if="steps.length && hint"
        class="text-caption grey--text mb-3"
      >
        {{ hint }}
      </div>

      <div
        v-for="s in steps"
        :key="s.key"
        class="d-flex align-center mb-3"
      >
        <v-chip
          v-if="s.severity"
          x-small
          label
          :color="severityColor(s.severity)"
          text-color="white"
          class="mr-3 flex-shrink-0"
        >
          {{ severityLabel(s.severity) }}
        </v-chip>

        <div class="mr-3">
          <div class="text-subtitle-2">
            {{ s.label }}
          </div>
          <div class="text-caption grey--text">
            {{ s.description }}
          </div>
        </div>

        <v-spacer />

        <v-btn
          v-if="actionOf(s)"
          small
          outlined
          color="mcsprimary"
          class="flex-shrink-0"
          :disabled="fired.includes(s.key)"
          @click="run(s)"
        >
          {{ fired.includes(s.key) ? 'Started' : actionLabel(s) }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'NextStepList',
  props: {
    // Either raw CampaignNextStep objects, or audience gap items which carry the step on `fix`.
    steps: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: 'Checking…'
    },
    emptyText: {
      type: String,
      default: 'Nothing to do here.'
    },
    hint: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      fired: []
    };
  },
  methods: {
    // A gap item wraps its action in `fix`; a campaign next step IS the action.
    actionOf(s) {
      const step = s.fix || s;
      return step && step.action ? step : null;
    },
    actionLabel(s) {
      const step = this.actionOf(s);
      if (!step) return '';
      // A step's `label` is the headline ("The Happy Hour idea page can't be generated yet"); `actionLabel`
      // is what the button says. Only the older steps conflate the two, so it wins when it's there —
      // otherwise a gap fix's button ends up repeating the sentence next to it.
      if (step.actionLabel) return step.actionLabel;
      if (step.label && s.fix) return step.label; // gap fixes carry their own button label
      if (step.action === 'addPage') return 'Create page';
      if (step.action === 'generateTemplates') return 'Generate templates';
      if (step.action === 'generateBlogPost') return 'Write blog post';
      if (step.action === 'link') return 'Open';
      return 'Do it';
    },
    severityColor(severity) {
      const s = this.$enums.AUDIENCE.GAP_SEVERITY[severity];
      return s ? s.color : 'grey';
    },
    severityLabel(severity) {
      const s = this.$enums.AUDIENCE.GAP_SEVERITY[severity];
      return s ? s.label : '';
    },
    run(s) {
      const step = this.actionOf(s);
      if (!step) return;

      // "link" steps always open in a NEW TAB — external URLs as-is, internal routes resolved to a full href.
      if (step.action === 'link') {
        const href = step.external ? step.url : this.$router.resolve(step.url).href;
        window.open(href, '_blank', 'noopener');
        return;
      }

      // Fire-and-forget: mark it started immediately, don't block on the background job.
      this.fired = [...this.fired, s.key];
      this.$emit('run', s);
    },
    // Called by the host when its dispatch failed, so the button becomes clickable again.
    unfire(key) {
      this.fired = this.fired.filter((k) => k !== key);
    }
  }
};
</script>
